import { ChevronRight, Ellipsis, User } from "lucide-react";
import type {
  MomentButton,
  MomentCarouselItem,
  MomentCommerceIntro,
  MomentCoupon,
  MomentListItem,
  MomentMessageType,
  MomentMessageTypeOption,
} from "../data/momentOptions";
import { getMaxButtons } from "../data/momentOptions";
import BasicTextPreview from "./preview/BasicTextPreview";
import WideMessagePreview from "./preview/WideMessagePreview";
import WideListPreview from "./preview/WideListPreview";
import CarouselFeedPreview from "./preview/CarouselFeedPreview";
import CarouselCommercePreview from "./preview/CarouselCommercePreview";

type Props = {
  channelLabel: string;
  messageName: string;
  body: string;
  messageTitle: string;
  imagePreview: string | null;
  typeOption: MomentMessageTypeOption;
  messageType: MomentMessageType;
  buttons: MomentButton[];
  buttonsEnabled: boolean;
  ageAuthEnabled: boolean;
  adFlag: boolean;
  shareFlag: boolean;
  couponEnabled: boolean;
  coupon: MomentCoupon;
  moreButtonEnabled: boolean;
  listItems: MomentListItem[];
  carouselItems: MomentCarouselItem[];
  commerceIntroEnabled: boolean;
  commerceIntro: MomentCommerceIntro;
};

export default function MomentPhonePreview({
  channelLabel,
  body,
  messageTitle,
  imagePreview,
  messageType,
  buttons,
  buttonsEnabled,
  adFlag,
  shareFlag,
  couponEnabled,
  coupon,
  moreButtonEnabled,
  listItems,
  carouselItems,
  commerceIntroEnabled,
  commerceIntro,
}: Props) {
  const isCarousel =
    messageType === "CAROUSEL_FEED_MESSAGE" ||
    messageType === "CAROUSEL_COMMERCE_MESSAGE";

  const channelName = channelLabel.trim() || "(프로필)";

  // buttonsEnabled 기반 displayButtons 계산 + 기본 텍스트 표시
  const displayButtons = (() => {
    if (!buttonsEnabled) return [];
    const max = getMaxButtons(messageType);
    return buttons.slice(0, max).map((btn, index) => ({
      ...btn,
      title: btn.title.trim() || `버튼${index + 1}`,
    }));
  })();

  return (
    <div className="tw-sticky tw-top-4 tw-w-full tw-space-y-4">
      <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-min-h-[800px] tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-[360px] tw-mx-auto tw-bg-[#bacee0] tw-h-[720px] tw-rounded-[30px] tw-overflow-hidden tw-border-8 tw-border-gray-800 tw-relative tw-shadow-xl">
          {/* 상태바 */}
          <div className="tw-h-6 tw-bg-[#bacee0] tw-flex tw-justify-between tw-items-center tw-px-4 tw-text-[10px] tw-font-medium tw-text-gray-700">
            <span>12:00</span>
            <div className="tw-flex tw-gap-1">
              <span>5G</span>
              <span>100%</span>
            </div>
          </div>

          {/* 채팅 헤더 */}
          <div className="tw-h-12 tw-bg-[#a9bdce] tw-flex tw-items-center tw-px-3 tw-gap-2 tw-shadow-sm">
            <div className="tw-p-1 tw-rounded-full">
              <ChevronRight className="tw-w-6 tw-h-6 tw-text-gray-700 tw-rotate-180" />
            </div>
            <div className="tw-flex-1">
              <span className="tw-font-semibold tw-text-gray-800">브랜드</span>
            </div>
            <Ellipsis className="tw-w-6 tw-h-6 tw-text-gray-700" />
          </div>

          {/* 채팅 내용 */}
          <div className="tw-px-3 tw-pt-5 tw-pb-3 tw-overflow-y-auto tw-h-[calc(100%-48px-24px)]">
            <div className="tw-flex tw-gap-2">
              {/* 프로필 아이콘 */}
              <div className="tw-w-9 tw-h-9 tw-bg-white tw-rounded-[14px] tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-overflow-hidden tw-border tw-border-gray-100">
                <User className="tw-w-5 tw-h-5 tw-text-gray-400" />
              </div>

              {/* 메시지 컬럼 */}
              <div className={`tw-flex tw-flex-col tw-gap-1 tw-w-full ${isCarousel ? "" : "tw-max-w-[280px]"}`}>
                <span className="tw-text-[11px] tw-text-gray-600 tw-ml-1">
                  {adFlag ? `(광고) ${channelName}` : channelName}
                </span>

                {messageType === "BASIC_TEXT_MESSAGE" && (
                  <BasicTextPreview
                    body={body}
                    displayButtons={displayButtons}
                    shareFlag={shareFlag}
                    couponEnabled={couponEnabled}
                    coupon={coupon}
                  />
                )}

                {messageType === "WIDE_MESSAGE" && (
                  <WideMessagePreview
                    body={body}
                    messageTitle={messageTitle}
                    imagePreview={imagePreview}
                    displayButtons={displayButtons}
                    shareFlag={shareFlag}
                    couponEnabled={couponEnabled}
                    coupon={coupon}
                  />
                )}

                {messageType === "WIDE_LIST_MESSAGE" && (
                  <WideListPreview
                    messageTitle={messageTitle}
                    listItems={listItems}
                    displayButtons={displayButtons}
                    shareFlag={shareFlag}
                    couponEnabled={couponEnabled}
                    coupon={coupon}
                  />
                )}

                {messageType === "CAROUSEL_COMMERCE_MESSAGE" && (
                  <CarouselCommercePreview
                    commerceIntroEnabled={commerceIntroEnabled}
                    commerceIntro={commerceIntro}
                    carouselItems={carouselItems}
                    shareFlag={shareFlag}
                    moreButtonEnabled={moreButtonEnabled}
                  />
                )}

                {messageType === "CAROUSEL_FEED_MESSAGE" && (
                  <CarouselFeedPreview
                    carouselItems={carouselItems}
                    moreButtonEnabled={moreButtonEnabled}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-bg-blue-50 tw-p-3 tw-rounded-lg tw-text-xs tw-text-blue-900">
        <p className="tw-font-semibold tw-mb-1">미리보기 안내</p>
        <p>실제 카카오모먼트 발송 화면과 세부 여백은 다를 수 있습니다.</p>
      </div>
    </div>
  );
}
